/**
 * Sheet Component
 *
 * Bottom sheet overlay built on `@expo/ui`'s BottomSheet: a native SwiftUI sheet
 * on iOS, a Material 3 ModalBottomSheet on Android, and a vaul drawer on web.
 *
 * Presents the same `visible` / `onClose` contract as `./modal`, so call sites
 * move between the two without restructuring.
 *
 * Motion, the drag handle and the backdrop are owned by each platform — do not
 * reimplement them here. This is also why the sheet honours system reduce-motion
 * settings for free.
 *
 * `visible` is translated into imperative present/dismiss calls rather than passed
 * down as an index, which is what lets the close animate on Android. See the effect
 * below before changing it back.
 */

import { BottomSheetModal, BottomSheetView } from '@expo/ui/community/bottom-sheet';
import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useAppTheme } from '@/providers/theme-provider';

/**
 * Opens half-height and drags up to near-full, leaving a strip of scrim visible.
 * Android only supports partial/expanded, so it maps to ~50% and full-screen there.
 */
export const SHEET_SNAP_POINTS: (string | number)[] = ['50%', '90%'];

export interface SheetProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Whether the sheet can be dismissed by swiping down or tapping the backdrop.
   * On iOS these cannot be separated — SwiftUI drives both from one flag.
   * @default true
   */
  dismissible?: boolean;
  /**
   * Heights the sheet can rest at, bottom to top. Two or more make it expandable
   * by swiping up — usually `SHEET_SNAP_POINTS`.
   *
   * Omit to size the sheet to its content, which is mutually exclusive with snap
   * points: iOS cannot combine `fitToContents` with explicit detents. Content
   * that is reliably taller than one screen wants snap points; a short, variable
   * list is usually better sized to content.
   */
  snapPoints?: (string | number)[];
  testID?: string;
}

export function Sheet({
  visible,
  onClose,
  children,
  dismissible = true,
  snapPoints,
  testID,
}: SheetProps) {
  const theme = useAppTheme();
  const expandable = snapPoints != null && snapPoints.length > 0;
  const sheetRef = useRef<BottomSheetModal>(null);

  // Open and close imperatively rather than through an `index` prop: only the
  // imperative path awaits the native dismiss animation before unmounting. Driving
  // the prop to -1 instead tears Android's Compose sheet down on the spot, so the
  // sheet vanishes rather than sliding away. iOS and web animate either way.
  useEffect(() => {
    if (visible) {
      sheetRef.current?.present();
    } else {
      sheetRef.current?.dismiss();
    }
  }, [visible]);

  return (
    <BottomSheetModal
      ref={sheetRef}
      onClose={onClose}
      enablePanDownToClose={dismissible}
      snapPoints={snapPoints}
      // vaul defaults to a white sheet on web, so the surface colour is explicit.
      backgroundStyle={{ backgroundColor: theme.colors.surface }}
    >
      {/* BottomSheetView strips its own flex styles when sizing to content,
          so flex:1 here fills a snap-pointed sheet and is a no-op otherwise. */}
      <BottomSheetView style={{ flex: 1 }}>
        {/* Fit-to-content measures intrinsic height, which a flexed child would collapse. */}
        <View testID={testID} style={expandable ? { flex: 1 } : undefined}>
          {children}
        </View>
      </BottomSheetView>
    </BottomSheetModal>
  );
}

Sheet.Header = function SheetHeader({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <View
      style={{ padding: 20, paddingBottom: 16 }}
      className={`border-b border-border ${className}`}
      {...props}
    >
      {children}
    </View>
  );
};

Sheet.Content = function SheetContent({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <View style={{ padding: 20 }} className={className} {...props}>
      {children}
    </View>
  );
};

/**
 * Padded, top-bordered action area. Unlike `Modal.Footer` this does not force a
 * right-aligned row — sheet footers are commonly a single full-width button.
 */
Sheet.Footer = function SheetFooter({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <View
      style={{ padding: 20, paddingTop: 16, gap: 8 }}
      className={`border-t border-border ${className}`}
      {...props}
    >
      {children}
    </View>
  );
};

export default Sheet;
